import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  LocalActionModel,
  SelectFieldInput,
  SelectFieldOutput,
} from '@trackback/widgets';
import { isEqual } from 'lodash-es';
import { combineLatest, of } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  shareReplay,
  switchMap,
  takeUntil,
  filter,
} from 'rxjs/operators';
import { BaseFormControlWidgetComponent } from '../base-form-control-widget.component';
import { ParsePipe } from '@app/pipes/parse.pipe';
import {
  MatOptionModule,
  MatPseudoCheckbox,
  MatRipple,
} from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { DynamicWidgetDirective } from '@app/directives/dynamic-widget.directive';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { AsyncPipe } from '@angular/common';
import { Register } from '@app/utils/type-registry';
import { toSignal } from '@angular/core/rxjs-interop';

@Register('SelectField')
@Component({
  selector: 'tb-select-field',
  templateUrl: './select-field.component.html',
  styleUrls: ['./select-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    DynamicWidgetDirective,
    MatSelectModule,
    MatCheckboxModule,
    MatOptionModule,
    AsyncPipe,
    ParsePipe,
    MatPseudoCheckbox,
    MatRipple,
  ],
})
export class SelectFieldComponent
  extends BaseFormControlWidgetComponent<SelectFieldInput, SelectFieldOutput>
  implements OnInit
{
  protected externalToInternal(next: unknown) {
    if (this.input?.multiple) {
      const isNullish = next === null || next === undefined;
      const isAllToken = next === 'all';
      if (isNullish || isAllToken) {
        const optionKeys = this.getOptionsKeyValues();
        if (this.input.allSelectedAsNull || isAllToken) {
          const options = this.parsedOptions?.() || [];
          return options.map(Option => Option[optionKeys.valueKey]);
        }
        return [];
      }
    }
    return next;
  }
  public readonly parsedOptions$ = this.input$.pipe(
    switchMap(input => this.parse(input?.options ?? [])),
    map(options =>
      Array.isArray(options) ? options.filter(option => !!option) : []
    ),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    })
  );
  public readonly parsedOptions = toSignal(this.parsedOptions$);

  public readonly allChecked$ = combineLatest([
    this.parsedOptions$,
    this.value$,
    this.input$,
  ]).pipe(
    map(([options, value, input]) => {
      if (value === null && input.multiple && input.allSelectedAsNull) {
        return true;
      }
      if (Array.isArray(value)) {
        return options.length === value.length;
      }
      return false;
    })
  );
  public readonly allChecked = toSignal(this.allChecked$);

  public readonly noneSelected$ = combineLatest([
    this.value$.pipe(filter(value => value !== undefined)),
    this.input$,
  ]).pipe(
    map(([value, input]) =>
      !!input?.multiple && (value == null || (Array.isArray(value) && value.length === 0))
    )
  );
  public readonly noneSelected = toSignal(this.noneSelected$, { initialValue: false });

  internalToExternal(next?: unknown) {
    if (this.input.multiple && this.input.allSelectedAsNull) {
      if (this._formControl.value?.length === this.parsedOptions().length) {
        return null;
      }
    }

    return next;
  }

  async ngOnInit() {
    await super.ngOnInit();

    const optionKeys = this.getOptionsKeyValues();

    if (this.input.multiple) {
      this.allChecked$
        .pipe(takeUntil(this.destroyed$))
        .subscribe(newValue => this.updateOutput({ allSelected: newValue }));
    }

    this.parsedOptions$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(parsedOptions => {
        this.updateOutput({
          options: parsedOptions,
        } as Partial<SelectFieldOutput>);
      });

    if (this.input.defaultValue) {
      this.parsedOptions$
        .pipe(
          switchMap(() => this.parse(this.input.defaultValue)),
          distinctUntilChanged(isEqual)
        )
        .subscribe(parsedDefaultValue => {
          this.updateLabelByValue(parsedDefaultValue);
          if (this.input.multiple && parsedDefaultValue === 'all') {
            this._formControl.patchValue(
              this.parsedOptions().map(Option => Option[optionKeys.valueKey])
            );
          }
        });
    }
  }

  toggleAllSelection() {
    if (!this.allChecked()) {
      const optionKeys = this.getOptionsKeyValues();
      this._formControl.setValue(
        this.parsedOptions().map(Option => Option[optionKeys.valueKey])
      );
    } else {
      this._formControl.setValue([]);
    }
    this._cd.detectChanges();
  }

  updateLabelByValue(value: unknown) {
    const optionKeys = this.getOptionsKeyValues();
    const index = this.parsedOptions().findIndex(
      e => e[optionKeys.valueKey] === value
    );

    if (this.parsedOptions()[index] != null) {
      this.updateOutput({
        label: this.parsedOptions()[index][optionKeys.labelKey],
      });
    }
  }

  handleResetAction(action: LocalActionModel) {
    if (this.input.multiple && !this.allChecked()) {
      const optionKeys = this.getOptionsKeyValues();
      this._formControl.setValue(
        this.parsedOptions().map(Option => Option[optionKeys.valueKey])
      );
      this._cd.detectChanges();
    } else {
      super.handleResetAction(action);
    }
    return of(null);
  }

  handleSetValueAction(action: LocalActionModel) {
    if (action.payload === 'all' && this.input.multiple) {
      const optionKeys = this.getOptionsKeyValues();
      this._formControl.patchValue(
        this.parsedOptions().map(Option => Option[optionKeys.valueKey])
      );
      this._cd.detectChanges();
    } else {
      this._value = action.payload;
    }
    return of(null);
  }

  /**
   * Gets the options object keys for the label and the value. Allowing the consumer
   * to change the values at creation time, e.g not enforcing the keys to be label
   * and value.
   *
   *
   * @returns {{ labelKey: string, valueKey: string }}
   */
  private getOptionsKeyValues(): { labelKey: string; valueKey: string } {
    return {
      labelKey:
        this.input.optionsLabelKey != null
          ? this.input.optionsLabelKey
          : 'label',
      valueKey:
        this.input.optionsValueKey != null
          ? this.input.optionsValueKey
          : 'value',
    };
  }
}
